import { ReactNode } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/ArrowLeftIcon'
import ChartBarIcon from '@heroicons/react/outline/ChartBarIcon'
import ChartSquareBarIcon from '@heroicons/react/outline/ChartSquareBarIcon'
import CogIcon from '@heroicons/react/outline/CogIcon'
import ColorSwatchIcon from '@heroicons/react/outline/ColorSwatchIcon'
import CreditCardIcon from '@heroicons/react/outline/CreditCardIcon'
import DocumentSearchIcon from '@heroicons/react/outline/DocumentSearchIcon'
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon'
import GlobeAltIcon from '@heroicons/react/outline/GlobeAltIcon'
import InboxInIcon from '@heroicons/react/outline/InboxInIcon'
import KeyIcon from '@heroicons/react/outline/KeyIcon'
import LockClosedIcon from '@heroicons/react/outline/LockClosedIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TemplateIcon from '@heroicons/react/outline/TemplateIcon'
import UserGroupIcon from '@heroicons/react/outline/UserGroupIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import UsersIcon from '@heroicons/react/outline/UsersIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'
import FontSizeIcon from 'remixicon-react/FontSizeIcon'

import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { Colors } from '../Editor/Colors'
import { ManageSpacesListSidebar } from '../Editor/ManageSpacesListSidebar'
import { SpaceSettingsSidebar } from '../Editor/ManageSpacesSidebar'
import { Typography } from '../Editor/Typography'
import { AppsSidebarContent } from './AppsSidebarContent'

export interface SidebarItem {
  title: string
  description?: string
  icon: JSX.Element
  path?: string
  href?: string
  exact?: boolean
  children?: Array<SidebarItem>
  component?: ReactNode
  featureFlag?: string
  arrow?: boolean
}

export const sidebars: Array<SidebarItem> = [
  {
    title: 'Back to community',
    icon: <ArrowLeftIcon />,
    path: '/',
    exact: true,
  },
  {
    title: 'People',
    icon: <UserIcon />,
    path: '/manage/people',
    children: [
      {
        title: 'Members',
        icon: <UserGroupIcon />,
        path: '/manage/people/members',
      },
      {
        title: 'Administrators',
        icon: <UsersIcon />,
        path: '/manage/people/admin',
      },
      {
        title: 'Invitations',
        icon: <InboxInIcon />,
        path: '/manage/people/invitations',
      },
      {
        title: 'Profile fields',
        icon: <UserGroupIcon />,
        path: '/manage/people/fields',
        featureFlag: FeaturesList.CustomProfileFields,
      },
    ],
  },
  {
    title: 'Content',
    icon: <DocumentTextIcon />,
    path: '/manage/content',
    children: [
      {
        title: 'Posts',
        icon: <DocumentTextIcon />,
        path: '/manage/content/posts',
      },
      {
        title: 'Spaces',
        icon: <ViewGridIcon />,
        path: '/manage/content/spaces',
      },
    ],
  },
  {
    title: 'Editor',
    icon: <PencilAltIcon />,
    path: '/manage/editor',
    description:
      'Structure and configure the overall look and feel of your community.',
    featureFlag: FeaturesList.PageCustomizer,
    children: [
      {
        title: 'Spaces',
        icon: <DocumentTextIcon />,
        path: '/manage/editor/spaces',
        component: <ManageSpacesListSidebar />,
        children: [
          {
            title: 'Space',
            icon: <DocumentTextIcon />,
            path: '/manage/editor/spaces/:id',
            component: <SpaceSettingsSidebar />,
            children: [
              {
                title: 'Page Settings',
                icon: <UserGroupIcon />,
                path: '/manage/editor/spaces/:id/members',
              },
              {
                title: 'Appearance',
                icon: <UsersIcon />,
                path: '/manage/editor/spaces/:id/appearance',
              },
              {
                title: 'Seo',
                icon: <InboxInIcon />,
                path: '/manage/editor/spaces/:id/seo',
              },
            ],
          },
        ],
      },
      {
        title: 'Navigation',
        icon: <TemplateIcon />,
        path: '/manage/editor/navigation',
      },
      {
        title: 'Colors',
        icon: <ColorSwatchIcon />,
        path: '/manage/editor/colors',
        component: <Colors />,
      },
      {
        title: 'Typography',
        icon: <FontSizeIcon />,
        path: '/manage/editor/typography',
        component: <Typography />,
      },
    ],
  },
  {
    title: 'Settings',
    icon: <CogIcon />,
    path: '/manage/settings',
    children: [
      {
        title: 'Community settings',
        icon: <UserIcon />,
        path: '/manage/settings/network',
      },
      {
        title: 'Authentication',
        icon: <KeyIcon />,
        path: '/manage/settings/authentication',
      },
      {
        title: 'Domain',
        icon: <GlobeAltIcon />,
        path: '/manage/settings/domain',
      },
      {
        title: 'Moderation',
        icon: <ShieldCheckIcon />,
        path: '/manage/settings/moderation',
      },
      {
        title: 'SEO Settings',
        path: '/manage/settings/seo',
        icon: <DocumentSearchIcon />,
      },
      {
        title: 'Security',
        icon: <LockClosedIcon />,
        path: '/manage/settings/security',
      },
      {
        title: 'Billing',
        href: '/admin/community/billing',
        icon: <CreditCardIcon />,
        arrow: true,
      },
    ],
  },
  {
    title: 'Reports',
    icon: <ChartSquareBarIcon />,
    path: '/manage/reports',
    children: [
      {
        title: 'Audit logs',
        path: '/manage/reports/audit-logs',
        icon: <ShieldExclamationIcon />,
        featureFlag: FeaturesList.AuditLogs,
      },
      {
        title: 'Email logs',
        path: '/manage/reports/email-logs',
        icon: <InboxInIcon />,
        featureFlag: FeaturesList.AuditLogs,
      },
      {
        title: 'Analytics',
        href: '/admin/community/analytics',
        icon: <ChartBarIcon />,
        arrow: true,
      },
    ],
  },
  {
    title: 'Apps',
    icon: <ViewGridIcon />,
    path: '/manage/apps',
    component: <AppsSidebarContent />,
  },
]

export const virtualSidebarRoot: SidebarItem = {
  title: '/',
  icon: <div />,
  path: '/manage',
  children: sidebars,
}
